import React, { HTMLAttributes } from 'react';
import prettyBytes from 'pretty-bytes';

import { ImmutableEntity } from '@uncinc/react-drupal-core/types';
import { Container, TitleWithText, useWindowDimensions } from '@uncinc/uncinc-react-kitchen-sink';

import { DynamicMediaItem } from '../../../components';
import { SCREEN_NORMAL, SCREEN_TABLET } from '../../../config/constants';
import type { ParagraphDownloads as Type } from '../../../types/uncinc_profile/paragraphs';

import './style.scss';

interface ParagraphDowloadsProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const ParagraphDownloads: React.FC<ParagraphDowloadsProps> = ({
  entity,
}) => {
  const paragraphId = entity.getIn(['id', 0, 'value']);
  const paragraphKey = `paragraph:${paragraphId}`;
  const paragraphDownloads = entity.toJS();

  const title = paragraphDownloads.field_title[0]?.value;
  const body = paragraphDownloads.field_body[0]?.processed;
  const downloads = paragraphDownloads.field_downloads;
  const image = paragraphDownloads.field_media_item[0];

  const downloadItems = downloads.map((download) => {
    const bundle = download.entity.bundle[0].target_id;

    try {
      const [mimeType, mimeSubType] = download.mime_type.split('/');
      const source = bundle === 'document' ?
        download.entity.field_document[0].source :
        download.entity.image[0].url;
      const filename = bundle === 'document' ?
        download.entity.field_document[0].filename :
        download.entity.image[0].filename;
      const extension = filename.match(/\w+$/);

      return (
        {
          source,
          mimeType,
          filename,
          extension,
          mimeSubType,
          filesize: download.file_size,
          title: `${download.title}`,
        }
      );
    } catch (e: any) {
      console.error(`ERROR PARSING DOWNLOADS \n BUNDLE: ${bundle} \n ENTITY: ${JSON.stringify(download.entity)} \n MESSAGE: `, e);
      return {};
    }
  });

  return (
    <div id={paragraphKey} className="Paragraph ParagraphDownloads">
      <Container>
        <div className="row">
          <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
            <TitleWithText
              title={title}
              text={body}
            />
            <div className="Downloads">
              {image && (
                <div className="Downloads__previewWrapper">
                  <DynamicMediaItem media={image} className="Downloads__files__preview" />
                </div>
              )}
              <div className="Downloads__files">
                {downloadItems.map((download) => (
                  download.filename && (
                    <a className="Download" target="_blank" href={download.source} rel="noreferrer">
                      <span className={`Download__icon icon icon-download icon-${download.mimeType} icon-${download.mimeSubType}`} />
                      <div className="Download__content">
                        <span className="Download__title label">{download.title}</span>
                        <div className="Download__attributes">
                          {download.extension && (
                            <span className="Download__extension">{download.extension[0]}&nbsp;</span>
                          )}
                          {download.filesize && (
                            <span className="Download__filesize">({prettyBytes(download.filesize)})</span>
                          )}
                        </div>
                      </div>
                    </a>
                  )
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default ParagraphDownloads;
